import {
  CONVERSATION_OUTCOME_LABELS,
  roleAtLeast,
  type ChannelView,
  type ConversationView,
  type Product,
} from '@ai-sales/shared'
import { useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query'
import { useEffect, useState, type FormEvent } from 'react'
import { Link, useNavigate } from 'react-router'
import { fetchChannels } from '../api/agents.api.js'
import { fetchConversations, simulateInbound } from '../api/messaging.api.js'
import { fetchProducts } from '../api/products.api.js'
import { Badge } from '../components/badge.js'
import { Button } from '../components/button.js'
import { EmptyState } from '../components/data-table.js'
import { ErrorPanel } from '../components/error-panel.js'
import { Field, SelectField } from '../components/field.js'
import { PageHeader, Panel, Stat } from '../components/page.js'
import { Spinner } from '../components/spinner.js'
import { formatRelative, plural } from '../lib/format.js'
import { useCurrentWorkspace } from '../workspace/workspace-context.js'

type Tab = 'all' | 'needsOperator' | 'callCenter' | 'paused' | 'closed'

const TABS: { key: Tab; label: string; hint: string }[] = [
  { key: 'all', label: 'Все открытые', hint: 'Всё, что сейчас в работе' },
  {
    key: 'needsOperator',
    label: 'Ждут оператора',
    hint: 'Рабочая очередь: здесь нужен живой человек',
  },
  {
    key: 'callCenter',
    label: 'Передали в КЦ',
    hint: 'Клиент согласился на звонок — дальше работает колл-центр',
  },
  { key: 'paused', label: 'На паузе', hint: 'Агент в них молчит' },
  { key: 'closed', label: 'Закрытые', hint: 'История' },
]

/**
 * Список диалогов.
 *
 * Вкладки, а не набор галочек. У оператора есть ровно один вопрос при
 * открытии экрана — «что мне разбирать сейчас», — и ответ на него должен
 * стоить одно нажатие. Набор фильтров отвечает на этот вопрос только
 * после того, как человек сам сообразит, какие из них выставить.
 */
export function ConversationsPage() {
  const workspace = useCurrentWorkspace()
  const canAct = roleAtLeast(workspace.role, 'operator')

  const [tab, setTab] = useState<Tab>('all')
  const [search, setSearch] = useState('')
  const [channelId, setChannelId] = useState('')

  const channels = useQuery({
    queryKey: ['channels', workspace.id],
    queryFn: () => fetchChannels(workspace.id),
  })

  const query = {
    /**
     * Вкладка «Передали в КЦ» не ограничивает состояние диалога. Исход
     * и состояние — разные оси: клиент, согласившийся на звонок, мог
     * потом дописать, и диалог остался открытым, а мог и закрыться.
     * Отбор по открытым спрятал бы часть переданных.
     */
    ...(tab === 'callCenter'
      ? { outcome: 'handedToCallCenter' as const }
      : tab === 'closed'
        ? { state: 'closed' as const }
        : { state: 'open' as const }),
    ...(tab === 'needsOperator' ? { needsOperator: true } : {}),
    ...(tab === 'paused' ? { pausedOnly: true } : {}),
    ...(search.trim() ? { search: search.trim() } : {}),
    ...(channelId ? { channelId } : {}),
  }

  const conversations = useInfiniteQuery({
    queryKey: ['conversations', workspace.id, query],
    queryFn: ({ pageParam }) =>
      fetchConversations(workspace.id, {
        ...query,
        limit: 30,
        ...(pageParam ? { cursor: pageParam } : {}),
      }),
    initialPageParam: '',
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    /**
     * Список обновляется сам: оператор держит его открытым, а сообщения
     * приходят без его участия. Десять секунд — компромисс между «вижу
     * новое сразу» и «не долблю сервер». Обновление идёт только когда
     * открыта первая страница: догрузив историю, человек читает, и
     * перетасовывать её под ним нельзя.
     */
    refetchInterval: (q) => ((q.state.data?.pages.length ?? 1) > 1 ? false : 10_000),
  })

  /**
   * Счётчик очереди считается отдельно, чтобы он был виден с любой вкладки.
   *
   * Берём страницу в 50 записей и считаем их, а не спрашиваем «есть ли
   * хоть одна»: «1+» на плитке ничего не сообщает человеку, который решает,
   * браться ли сейчас. Больше пятидесяти показываем как «50+» — на таком
   * числе точность уже не меняет решения, а отдельный запрос ради неё
   * стоил бы дороже пользы.
   */
  const QUEUE_PROBE = 50

  const queueCount = useQuery({
    queryKey: ['conversations-queue', workspace.id],
    queryFn: () =>
      fetchConversations(workspace.id, {
        needsOperator: true,
        state: 'open',
        limit: QUEUE_PROBE,
      }),
    refetchInterval: 15_000,
  })

  const queueLabel =
    queueCount.data === undefined
      ? '…'
      : queueCount.data.nextCursor
        ? `${QUEUE_PROBE}+`
        : String(queueCount.data.items.length)

  const rows = conversations.data?.pages.flatMap((page) => page.items) ?? []
  const activeTab = TABS.find((item) => item.key === tab)

  /**
   * Внутренний канал — тот, в который можно подать сообщение «от клиента».
   * Пока он есть, показываем форму начала диалога: она нужна ровно на том
   * этапе, когда настоящего канала ещё нет, и исчезает сама, когда его
   * заводят.
   */
  const internalChannel = (channels.data ?? []).find((item) => item.provider === 'internal')

  return (
    <>
      <PageHeader
        title="Диалоги"
        description="Переписки с клиентами. Здесь оператор вмешивается: ставит паузу, пишет руками и возвращает диалог агенту."
      />

      <div className="mb-5 grid gap-3 sm:grid-cols-3">
        <Stat
          label="Ждут оператора"
          value={queueLabel}
          tone={queueCount.data?.items.length ? 'warn' : 'neutral'}
        />
        <Stat label="Показано" value={rows.length} />
        <Stat label="Каналов" value={channels.data?.length ?? '…'} />
      </div>

      {canAct && internalChannel && <StartDialog workspaceId={workspace.id} channel={internalChannel} />}

      <Panel className="mb-5">
        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap gap-1.5" role="tablist" aria-label="Что показывать">
            {TABS.map((item) => (
              <Button
                key={item.key}
                size="sm"
                variant={tab === item.key ? 'primary' : 'ghost'}
                role="tab"
                aria-selected={tab === item.key}
                onClick={() => setTab(item.key)}
              >
                {item.label}
              </Button>
            ))}
          </div>

          <p className="text-xs text-muted">{activeTab?.hint}</p>

          <div className="grid gap-3 md:grid-cols-2">
            <Field
              label="Поиск"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Телефон или имя"
              hint="Телефон можно писать как угодно — сравниваются только цифры"
            />
            <SelectField
              label="Канал"
              value={channelId}
              onChange={(event) => setChannelId(event.target.value)}
            >
              <option value="">Все каналы</option>
              {(channels.data ?? []).map((channel) => (
                <option key={channel.id} value={channel.id}>
                  {channel.name}
                </option>
              ))}
            </SelectField>
          </div>
        </div>
      </Panel>

      {conversations.error && (
        <ErrorPanel error={conversations.error} onRetry={() => void conversations.refetch()} />
      )}

      {conversations.isLoading ? (
        <div className="flex items-center gap-2 rounded-md border border-line bg-surface p-8 text-sm text-muted">
          <Spinner /> Загружаем диалоги…
        </div>
      ) : rows.length === 0 ? (
        <EmptyState
          title="Диалогов нет"
          description={
            tab === 'needsOperator'
              ? 'Очередь пуста — ни один диалог не ждёт человека.'
              : canAct && internalChannel
                ? 'Напишите первое сообщение в блоке «Начать диалог» выше — агент ответит, и диалог появится здесь.'
                : canAct
                  ? 'Диалог начинается с сообщения клиента. Заведите тест-канал в разделе «Каналы», чтобы проверить агента без WhatsApp.'
                  : 'Пока переписок нет.'
          }
        />
      ) : (
        <ul className="flex flex-col gap-2">
          {rows.map((row) => (
            <ConversationRow key={row.id} row={row} workspaceId={workspace.id} />
          ))}
        </ul>
      )}

      {conversations.hasNextPage && (
        <div className="mt-4">
          <Button
            loading={conversations.isFetchingNextPage}
            onClick={() => void conversations.fetchNextPage()}
          >
            Показать ещё
          </Button>
        </div>
      )}

      {rows.length > 0 && (
        <p className="mt-3 text-xs text-muted">
          {rows.length} {plural(rows.length, 'диалог', 'диалога', 'диалогов')}
        </p>
      )}
    </>
  )
}

/**
 * Начало диалога из админки.
 *
 * Диалог в LeadPilot заводит клиент, а не мы: он приходит из канала. Пока
 * настоящего канала нет, единственная дверь внутрь — тест-канал, и до сих
 * пор она была только на странице уже существующего диалога. Получалось
 * замкнутое место: чтобы завести первый диалог, нужно было открыть диалог.
 *
 * Поля заполнены заранее не для красоты. Проверка агента — это несколько
 * десятков прогонов подряд, и каждый раз придумывать номер значит каждый
 * раз ошибаться в формате; номер подставляется новый на каждую загрузку
 * страницы, чтобы очередная проверка начиналась с чистого разговора, а не
 * дописывалась в предыдущий.
 *
 * Первое сообщение — не «Hola», а шаблон лендинга целиком, с номером
 * заявки. Это не украшение: товар привязывается к диалогу разбором
 * первого сообщения, по группе оффера в номере заявки. Без неё товара
 * нет, и агент, которому запрещено выдумывать цену, сразу передаёт
 * клиента в колл-центр. Первый же живой прогон у ANR 24.09.2026
 * закончился ровно этим: человек нажал кнопку с заготовленным «сколько
 * стоит?» — и не получил ни слова в ответ. Группа оффера берётся из
 * товаров воркспейса, а не вписана числом: числа устаревают молча.
 */
function StartDialog({ workspaceId, channel }: { workspaceId: string; channel: ChannelView }) {
  const navigate = useNavigate()

  const products = useQuery({
    queryKey: ['products', workspaceId],
    queryFn: () => fetchProducts(workspaceId),
  })

  const [phone, setPhone] = useState(newPhone)
  const [name, setName] = useState('Luis')
  const [text, setText] = useState('')

  /**
   * Заготовка появляется, когда доехал список товаров, и не затирает
   * то, что человек уже набрал руками.
   */
  const sample = firstMessage(products.data)

  useEffect(() => {
    if (sample) setText((current) => (current === '' ? sample : current))
  }, [sample])

  const start = useMutation({
    mutationFn: () =>
      simulateInbound(workspaceId, channel.id, {
        phone: phone.trim(),
        text: text.trim(),
        ...(name.trim() ? { name: name.trim() } : {}),
      }),
    onSuccess: (result) => {
      void navigate(`/w/${workspaceId}/conversations/${result.conversationId}`)
    },
  })

  function submit(event: FormEvent) {
    event.preventDefault()
    if (!phone.trim() || !text.trim()) return
    start.mutate()
  }

  return (
    <Panel
      className="mb-5"
      title="Начать диалог"
      description={`Сообщение уйдёт в «${channel.name}» как настоящее входящее: те же проверки, та же очередь, тот же агент. Наружу ничего не отправляется.`}
    >
      <form onSubmit={submit} className="flex flex-col gap-3">
        <div className="grid gap-3 sm:grid-cols-[1fr_1fr]">
          <Field
            label="Телефон клиента"
            value={phone}
            onChange={(event) => setPhone(event.target.value)}
            maxLength={30}
            hint="Новый номер — новый диалог. Тот же номер продолжит прежний"
          />
          <Field
            label="Имя клиента"
            value={name}
            onChange={(event) => setName(event.target.value)}
            maxLength={200}
            hint="Необязательно — в WhatsApp его присылает сам клиент"
          />
        </div>

        <Field
          label="Первое сообщение"
          value={text}
          onChange={(event) => setText(event.target.value)}
          maxLength={4096}
          placeholder="Номер заявки 8841-21897. Здравствуйте! Интересует Urodoc."
          hint={
            withOffer(products.data)
              ? 'Номер заявки — из него определяется товар. Уберёте его — агент останется без цены и передаст клиента в колл-центр'
              : 'Ни у одного товара не указана группа оффера, поэтому товар по сообщению не определится. Впишите её в карточке товара'
          }
        />

        {start.error != null && <ErrorPanel error={start.error} />}

        <div className="flex flex-wrap items-center gap-3">
          <Button type="submit" variant="primary" loading={start.isPending}>
            Начать диалог
          </Button>
          <span className="text-xs text-muted">
            Откроется переписка — ответ агента появится в ней через несколько секунд.
          </span>
        </div>
      </form>
    </Panel>
  )
}

/**
 * Номер для проверки.
 *
 * Гватемала, мобильный диапазон: именно такие номера у клиентов, ради
 * которых всё и строится, и именно на таких проверяется разбор номера.
 */
function newPhone(): string {
  return `+5025${String(Math.floor(Math.random() * 10_000_000)).padStart(7, '0')}`
}

/** Первый товар, по которому диалог вообще может определиться. */
function withOffer(products: Product[] | undefined): Product | null {
  return products?.find((item) => item.offerGroupId?.trim()) ?? null
}

/**
 * Заготовка первого сообщения — тот самый шаблон, который подставляет
 * виджет на лендинге: источник, группа оффера, название товара.
 * Отличается от настоящего только источником: 8841 — выдуманная
 * кампания, и в отчётах проверочные диалоги видно по ней.
 */
function firstMessage(products: Product[] | undefined): string {
  const product = withOffer(products)

  if (!product) return ''

  return (
    `Номер заявки 8841-${product.offerGroupId}. Здравствуйте! Интересует ${product.name}. ` +
    'Хочу воспользоваться бесплатной консультацией и узнать подробнее.'
  )
}

const SENDER_PREFIX: Record<string, string> = {
  customer: 'Клиент',
  ai: 'Агент',
  operator: 'Оператор',
  system: 'Система',
}

function ConversationRow({ row, workspaceId }: { row: ConversationView; workspaceId: string }) {
  return (
    <li className="rounded-md border border-line bg-surface px-4 py-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <Link
            to={`/w/${workspaceId}/conversations/${row.id}`}
            className="font-medium text-accent hover:underline"
          >
            {row.contact.name || row.contact.phoneE164}
          </Link>
          {row.contact.name && (
            <span className="ml-2 font-mono text-xs text-muted">{row.contact.phoneE164}</span>
          )}
          <p className="mt-0.5 text-xs text-muted">
            {row.channelName}
            {row.contact.isBlacklisted && ' · в чёрном списке'}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-1.5">
          {row.outcome && <Badge tone="ok">{CONVERSATION_OUTCOME_LABELS[row.outcome]}</Badge>}
          {row.needsOperator && <Badge tone="warn">ждёт оператора</Badge>}
          {row.isPaused && <Badge tone="neutral">на паузе</Badge>}
          {row.state === 'closed' && <Badge tone="neutral">закрыт</Badge>}
        </div>
      </div>

      {row.lastMessage && (
        <p className="mt-1.5 line-clamp-2 text-sm text-ink-soft">
          <span className="text-muted">{SENDER_PREFIX[row.lastMessage.sender] ?? ''}: </span>
          {row.lastMessage.body || '(без текста)'}
        </p>
      )}

      {row.needsOperator && row.handoffReason && (
        <p className="mt-1 text-xs text-copper">Причина: {row.handoffReason}</p>
      )}

      <p className="mt-1 text-xs text-muted">
        {row.messageCount} {plural(row.messageCount, 'сообщение', 'сообщения', 'сообщений')} ·{' '}
        {formatRelative(row.updatedAt)}
      </p>
    </li>
  )
}
